import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import FormModal from '@shared/ui/FormModal';
import WorkspacePickerList from './workspace/WorkspacePickerList';

interface CandidateWorkspaceSelectionModalProps {
  candidateId: string;
  candidateName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface WorkspaceOption {
  workspace_id: string;
  workspace_name: string;
  isTracked: boolean;
}

const CandidateWorkspaceSelectionModal: React.FC<CandidateWorkspaceSelectionModalProps> = ({
  candidateId,
  candidateName,
  isOpen,
  onClose,
}) => {
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<Set<string>>(new Set());
  const [initialTracked, setInitialTracked] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const [wsResponse, trackingResponse] = await Promise.all([
          api.get('/workspaces'),
          api.get(`/workspace-candidate-tracking/candidate/${candidateId}/workspaces`),
        ]);

        const userWorkspaces = Array.isArray(wsResponse.data) ? wsResponse.data : [];
        const trackedIn: Set<string> = trackingResponse.data.success
          ? new Set<string>(trackingResponse.data.data.map((t: { workspace_id: string }) => t.workspace_id))
          : new Set<string>();

        const options: WorkspaceOption[] = userWorkspaces.map((ws: { id: string; name: string }) => ({
          workspace_id: ws.id,
          workspace_name: ws.name,
          isTracked: trackedIn.has(ws.id),
        }));

        setWorkspaces(options);
        setSelectedWorkspaces(new Set<string>(trackedIn));
        setInitialTracked(new Set<string>(trackedIn));
      } catch {
        setWorkspaces([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, candidateId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const toAdd = [...selectedWorkspaces].filter(id => !initialTracked.has(id));
      const toRemove = [...initialTracked].filter(id => !selectedWorkspaces.has(id));

      if (toAdd.length > 0) {
        await api.post('/workspace-candidate-tracking', {
          candidateId,
          workspaceIds: toAdd,
        });
      }

      if (toRemove.length > 0) {
        await api.delete('/workspace-candidate-tracking', {
          data: { candidateId, workspaceIds: toRemove },
        });
      }

      queryClient.invalidateQueries(['tracked-candidate-ids']);
      onClose();
    } catch {
      // Error handled silently - toast could be added
    } finally {
      setSaving(false);
    }
  };

  const toggleWorkspace = (wsId: string) => {
    setSelectedWorkspaces(prev => {
      const next = new Set(prev);
      if (next.has(wsId)) next.delete(wsId);
      else next.add(wsId);
      return next;
    });
  };

  return (
    <FormModal
      isOpen={isOpen}
      title="Track in workspaces"
      description={`Select workspaces to track ${candidateName}`}
      onClose={onClose}
      onSubmit={handleSave}
      size="md"
      isSubmitting={saving}
      submitDisabled={loading}
    >
      <WorkspacePickerList
        workspaces={workspaces.map(ws => ({ id: ws.workspace_id, name: ws.workspace_name }))}
        selectedIds={[...selectedWorkspaces]}
        onToggle={toggleWorkspace}
        loading={loading}
      />
    </FormModal>
  );
};

export default CandidateWorkspaceSelectionModal;
